import { TableCell, TableSortLabel, useTheme } from '@mui/material';
import { Checkbox } from '@/components/atoms';
import { TableColumn, TableProps } from '../Table.props';

export interface TableHeaderCellProps<T> {
  column: TableColumn<T>;
  sorting?: TableProps<T>['sorting'];
  allSelected?: boolean;
  onSelectAll?: (checked: boolean) => void;
  stickyPosition?: number;
}

/**
 * TableHeaderCell - Renders individual header cells including sortable headers
 *
 * Features:
 * - Handles sortable columns with sort indicators
 * - Uses render method for special columns (selection, actions)
 * - Applies proper styling and alignment
 */
export const TableHeaderCell = <T,>({
  column,
  sorting,
  allSelected,
  onSelectAll,
  stickyPosition,
}: TableHeaderCellProps<T>) => {
  const theme = useTheme();

  // Generate sticky styles if column is sticky
  // When a column is both sticky horizontally and the header is sticky vertically,
  // it needs the highest z-index to appear above both regular sticky headers and sticky body cells
  const stickyStyles = column.sticky
    ? {
        position: 'sticky' as const,
        [column.sticky]: `${stickyPosition}px`,
        zIndex: 10, // Sticky column + sticky header = highest z-index
        backgroundColor: theme.palette.M3.sys.light['background 2'],
        '&.MuiTableCell-root': {
          zIndex: 10, // Override parent TableHead selector
        },
      }
    : {};

  // Handle selection column with special checkbox header
  if (column.id === '__select') {
    return (
      <TableCell
        padding="checkbox"
        variant="headerCheckbox"
        sx={{
          ...stickyStyles,
          zIndex: column.sticky ? 10 : undefined, // Explicitly set for sticky columns
        }}
      >
        <Checkbox
          indeterminate={allSelected === undefined}
          checked={allSelected === true}
          onChange={(event) => onSelectAll?.(event.target.checked)}
        />
      </TableCell>
    );
  }

  // Handle actions column
  if (column.id === '__actions') {
    return (
      <TableCell
        variant="headerAction"
        sx={{
          ...stickyStyles,
          zIndex: column.sticky ? 10 : undefined, // Explicitly set for sticky columns
        }}
      >
        {column.label}
      </TableCell>
    );
  }

  // Handle regular columns
  return (
    <TableCell
      align={column.align}
      variant="header"
      sx={{
        width: column.width,
        minWidth: column.minWidth,
        ...stickyStyles,
        zIndex: column.sticky ? 10 : undefined, // Explicitly set for sticky columns
      }}
    >
      {column.sortable && sorting ? (
        <TableSortLabel
          active={sorting.orderBy === column.id}
          direction={sorting.orderBy === column.id ? sorting.order : 'asc'}
          onClick={() => sorting.onSort(column.id as string)}
        >
          {column.label}
        </TableSortLabel>
      ) : (
        column.label
      )}
    </TableCell>
  );
};
